import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { tvTemplateService } from '@/services/tv_template_service'
import { Loader2 } from 'lucide-react'
import { logger } from '@magnetar/utils'

interface CreateTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function CreateTemplateDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateTemplateDialogProps) {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !formData.template_name.trim()) return

    setIsLoading(true)
    try {
      const createData = {
        ...formData,
        ...(mode === 'provider' && viewModeId ? { provider_id: viewModeId } : {}),
      }
      const result = await tvTemplateService.createTemplate(createData, token)
      if (result.success) {
        onSuccess?.()
        setFormData({ template_name: '', description: '' })
        if (result.data?.template_id) {
          navigate(`/apps/tv-templates/${result.data.template_id}`)
        }
      }
    } catch (error) {
      logger.error('建立模板失敗', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>建立電視模板</DialogTitle>
            <DialogDescription>
              建立新的電視模板，之後可以編輯 VOD 列表並套用到設備
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template_name">模板名稱 *</Label>
              <Input
                id="template_name"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                placeholder="例如：標準電視模板"
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="描述這個模板的用途..."
                rows={3}
                disabled={isLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              取消
            </Button>
            <Button type="submit" disabled={isLoading || !formData.template_name.trim()}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              建立
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
